import { timezoneDateTime } from '@/helpers/datetime'
import { timeOfDayOptions, weatherOptions } from '@/resources/session-options'
import { Calendar, Clock, RotateCw } from 'lucide-react'
import { useCallback } from 'react'

export default function ManageChampionshipEventSessions({ event }) {
  const { sessions, championship } = event

  const getSessionRaceCount = useCallback(
    (sessionIndex) => {
      if (sessions[sessionIndex].type !== 'race') return ''

      return sessions.reduce((count, session, index) => {
        if (session.type !== 'race' || index >= sessionIndex) return count

        return count + 1
      }, 1)
    },
    [sessions],
  )

  const getSessionTimeOfDayOption = useCallback(
    (sessionIndex) =>
      timeOfDayOptions.find(
        (option) => option.name === sessions[sessionIndex].timeOfDay,
      ),
    [sessions],
  )

  const getSessionWeatherOption = useCallback(
    (sessionIndex) =>
      weatherOptions.find(
        (option) => option.name === sessions[sessionIndex].weather,
      ),
    [sessions],
  )

  const getSessionSplits = useCallback(
    (sessionIndex) =>
      championship.splits.filter((split) =>
        sessions[sessionIndex].splits.some(
          (s) => s.championshipSplitId === split.id,
        ),
      ),
    [championship.splits, sessions],
  )

  const getSessionStartTime = useCallback(
    (sessionIndex) => {
      if (!sessions[sessionIndex].targetDateTime) return null

      return timezoneDateTime({
        datetime: sessions[sessionIndex].targetDateTime,
        timezone: sessions[sessionIndex].targetTimezone,
        format: false,
      })
    },
    [sessions],
  )

  return (
    <div className="flex flex-col gap-3">
      <h4 className="text-h-4 font-semibold">Sessions</h4>

      <div className="flex flex-col gap-2">
        {sessions.map((session, index) => (
          <div
            key={session.id}
            className="flex flex-col flex-wrap items-center justify-between gap-4 text-nowrap rounded-sm border border-neutral-blue-700 bg-neutral-blue-800 p-4 capitalize sm:flex-row"
          >
            <div className="flex items-center gap-2">
              <div className="flex h-[36px] w-min min-w-[36px] items-center justify-center gap-1 rounded-sm bg-neutral-blue-700 font-medium">
                {session.type[0]}
                {getSessionRaceCount(index)}
              </div>

              <h6 className="text-h-6 font-medium">
                {session.type} {getSessionRaceCount(index)}
              </h6>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-2">
              <EventSessionStat
                icon={<Calendar className="size-5" />}
                text={
                  getSessionStartTime(index)?.format('ddd D MMM') ??
                  'Date required'
                }
                isAttention={!session.targetDateTime}
              />

              <EventSessionStat
                icon={<Clock className="size-5" />}
                text={
                  getSessionStartTime(index)?.format('HH:mm z') ?? 'Not set'
                }
                isAttention={!session.targetDateTime}
              />

              <EventSessionStat
                icon={<RotateCw className="size-5" />}
                text={
                  <span>
                    {session.duration} {session.durationType}
                    {session.duration !== '1' ? 's' : ''}
                  </span>
                }
              />

              {getSessionSplits(index).length > 0 && (
                <EventSessionStat
                  icon={
                    <div className="mr-[7px] flex items-center">
                      {getSessionSplits(index).map((split) => (
                        <div
                          key={split.id}
                          className="mr-[-7px] h-3 w-3 rounded-full hover:z-10"
                          style={{
                            backgroundColor: split.color ?? '#ffffff',
                          }}
                        />
                      ))}
                    </div>
                  }
                  text={
                    <span>
                      {getSessionSplits(index).length ===
                      event.championship.splits.length
                        ? 'All Splits'
                        : getSessionSplits(index)
                            .map((s) => s.name)
                            .join(',')}
                    </span>
                  }
                />
              )}

              {session.timeOfDay && (
                <EventSessionStat
                  icon={getSessionTimeOfDayOption(index)?.icon}
                  text={getSessionTimeOfDayOption(index)?.label}
                />
              )}

              {session.weather && (
                <EventSessionStat
                  icon={getSessionWeatherOption(index)?.icon}
                  text={getSessionWeatherOption(index)?.label}
                />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function EventSessionStat({ icon, text, isAttention = false }) {
  return (
    <div
      className={`flex items-center gap-1 text-p-1 font-medium ${isAttention ? 'text-error-500' : 'text-shades-0'}`}
    >
      <span className="stroke-white">{icon}</span> {text}
    </div>
  )
}
